import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import { bindActionCreators, Dispatch } from 'redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { Paginator } from 'primereact/paginator';
import { getDataTableSortFields, getSearchParamSortFields } from '../../helpers/sortHelper';
import { RouteWithParams } from '../../helpers/routing';
import { Routes } from '../../constants/routes';
import {
	REPORT_URL_PARAMS,
	REPORT_IDS,
	DATETIME_FORMAT,
	TASK_LIST_VIEWS as VIEWS,
	GANTT_ZOOM_LEVELS,
	DATE_FORMAT,
	SortColumnsType,
} from '../../constants/constants';
import Icon from '../Common/Icon';
import Button from '../Common/Button';
import { daysToDaysWeeks } from '../../helpers/dateTime';
import Moment from 'moment';
import './BaselineTaskListDesktop.scss';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import { Checkbox } from 'primereact/checkbox';
import DatePicker from '../Common/DatePicker/DatePicker';
import PushdownPreviewDrawer from '../PushdownPreview/PushdownPreviewDrawer';
import BulkPushdownDrawer from './BulkPushdown/BulkPushdownDrawer';
import BaselineImportDrawer from './Import/BaselineImportDrawer';
import OrderTasksDrawer from './OrderTasksDrawer';
import BaselineTaskListColumnDrawer from './BaselineTaskListColumnDrawer';
import Dockbar from '../Common/Dockbar/Dockbar';
import Modal from '../Common/Modal/Modal';
import DateRangePickerWrapper from '../Common/DateRangePicker/DateRangePickerWrapper';
import ActiveToggleForm from './ActiveToggleForm';
import { Dropdown } from 'primereact/dropdown';
import Notification from '../Common/Notification';
import { TruncateText } from '../../helpers/strings';
import GanttChartView from './GanttChart/GanttChartView';
import OpeningDateModal from './GanttChart/OpeningDateModal';
import BulkUpdateAttributesDrawer from './BulkUpdate/BulkUpdateAttributesDrawer';
import Tag from '../Common/Tag/Tag';
import ReactGA from 'react-ga4';
import { MultiSelect } from 'primereact/multiselect';
import { getMultiSelectFilterValues, getNullableOnChangeValue } from '../../helpers/multiselect';
import { classNames } from 'primereact/utils';
import { actions as userActions } from '../../actions/userActions';

// Type definitions
interface Baseline {
	id: number;
	name: string;
}

interface BaselineTask {
	id: number;
	name: string;
	IsChecked: boolean;
	isMilestone?: boolean;
	isActive: boolean;
	startDaysOut?: number;
	targetDaysOut: number;
	startStaticDate?: string;
	targetStaticDate?: string;
	ordinal?: number;
	baselineJobTitle?: string;
	additionalInfo?: string;
	links?: any[];
	department?: string;
	discipline?: string;
	community?: string;
	phase?: string;
	anticipatedEffort?: string;
	totalDependencies?: number;
	totalSubtasks?: number;
	totalResolvedFeedback?: number;
	createdDate: string;
	modifiedDate: string;
	tags?: any[];
}

interface FilterOption {
	id: number;
	name: string;
}

interface SearchParams {
	baselineId: string | number;
	name: string;
	additionalInfo: string;
	departments: number[];
	baselineJobTitles: number[];
	communities: number[];
	phases: number[];
	disciplines: number[];
	tags: number[];
	isActive: boolean | null;
	pageSize: number;
	pageNumber: number;
	createdDateStart?: Date | string | null;
	createdDateEnd?: Date | string | null;
	sortBy?: string;
	sortDir?: string;
}

interface FormFilters {
	isStartDateActive: boolean;
	isTaskStepsActive: boolean;
	isTaskEffortActive: boolean;
	isTaskFeedbackActive: boolean;
	isTaskDependenciesActive: boolean;
	isProjectWidgetActive: boolean;
}

interface ColumnOption {
	field: string;
	header: string;
	isFeatureEnabled: boolean;
	default: boolean;
	isSortable?: boolean;
	body?: (rowData: any) => JSX.Element | string | null;
}

interface DispatchProps {
	userActions: typeof userActions;
}

interface ComponentProps {
	subscriptionId: string;
	baselines: Baseline[];
	columnOrder: string[];
	onColumnOrderChange: (order: string[]) => void;
	filesActions: any;
	baselineTasks: BaselineTask[];
	taskTagActions: any;
	totalCount: number;
	isFetchingBaselines: boolean;
	isFetchingTasks: boolean;
	isLoadingBaselines: boolean;
	isLoadingTasks: boolean;
	isFetchingFilters: boolean;
	isLoadingFilters: boolean;
	updateSearchParams: (name: string, value: any) => void;
	updateParamsAndSearch: (name?: string, value?: any) => void;
	searchParams: SearchParams;
	departments: FilterOption[];
	baselineJobTitles: FilterOption[];
	communities: FilterOption[];
	phases: FilterOption[];
	disciplines: FilterOption[];
	tags: FilterOption[];
	showTask: (id?: number) => void;
	onToggleCheckedTask: (id: number) => void;
	onMasterCheckboxToggle: () => void;
	allTasksChecked: boolean;
	formFilters: FormFilters;
	checkedTasks: BaselineTask[];
	onBulkPushdownComplete: () => void;
	onComplete: () => void;
	hasNoFilters: () => boolean;
	clearFilters: () => void;
	onSort: (sortBy: string, sortDir: string) => void;
	updateCreatedDateParams: (start: Date | null, end: Date | null) => void;
	onViewSelectorChange: (viewName: string) => void;
	onViewFiltersClick: () => void;
	lastFilterChange: Date;
	selectedView: string;
	clearAllCheckedTasks: () => void;
}

type Props = ComponentProps & DispatchProps & RouteComponentProps;

const BaselineTaskListDesktop: React.FC<Props> = (props) => {
	const dataTableRef = useRef<DataTable<any>>(null);

	const [cols, setCols] = useState<ColumnOption[]>([]);
	const [columnOptions, setColumnOptions] = useState<Array<{ label: string; value: ColumnOption }>>([]);
	const [showPreviewDrawer, setShowPreviewDrawer] = useState<boolean>(false);
	const [isBulkPushdownDrawerOpen, setIsBulkPushdownDrawerOpen] = useState<boolean>(false);
	const [isBulkUpdateAttributesDrawerOpen, setIsBulkUpdateAttributesDrawerOpen] = useState<boolean>(false);
	const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState<boolean>(false);
	const [isImportDrawerOpen, setIsImportDrawerOpen] = useState<boolean>(false);
	const [isOrderTasksDrawerOpen, setIsOrderTasksDrawerOpen] = useState<boolean>(false);
	const [orderTasksTargetDaysOut, setOrderTasksTargetDaysOut] = useState<string>('');
	const [orderTasksTargetStaticDate, setOrderTasksTargetStaticDate] = useState<string>('');
	const [showDateRangePicker, setShowDateRangePicker] = useState<boolean>(false);
	const [openingDate, setOpeningDate] = useState<Date | null>(null);
	const [showOpeningDateModal, setShowOpeningDateModal] = useState<boolean>(false);
	const [ganttZoomLevel, setGanttZoomLevel] = useState<number>(GANTT_ZOOM_LEVELS.Month.value);
	const [dropFilterChanged, setDropFilterChanged] = useState<boolean>(false);
	const [dateRange, setDateRange] = useState<{
		start: Date | null;
		end: Date | null;
		name: string;
	}>({
		start: null,
		end: null,
		name: '',
	});
	const [selectedTask, setSelectedTask] = useState<BaselineTask | null>(null);
	const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	// useEffect to replace componentDidMount
	useEffect(() => {
		buildColumnOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// useEffect to replace componentDidUpdate for form filters and column order changes
	useEffect(() => {
		buildColumnOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		props.formFilters.isStartDateActive,
		props.formFilters.isTaskStepsActive,
		props.formFilters.isTaskEffortActive,
		props.formFilters.isTaskFeedbackActive,
		props.formFilters.isTaskDependenciesActive,
		props.columnOrder,
		refreshKey,
	]);

	// DataTable caches the dragged column order internally and that cache wins over the rendered
	// children, so it has to be re-synced whenever the column set or order changes.
	useEffect(() => {
		dataTableRef.current?.resetColumnOrder();
	}, [
		props.columnOrder,
		props.formFilters.isStartDateActive,
		props.formFilters.isTaskStepsActive,
		props.formFilters.isTaskEffortActive,
		props.formFilters.isTaskFeedbackActive,
		props.formFilters.isTaskDependenciesActive,
	]);

	// useEffect for scrolling to selected row
	useEffect(() => {
		if (selectedRowIndex !== null && dataTableRef.current !== null) {
			const targetRow = dataTableRef.current?.getElement()?.querySelector(`.p-datatable-tbody > tr:nth-child(${selectedRowIndex + 1})`);
			if (targetRow) {
				targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}, [selectedRowIndex]);

	const getStaticColumns = useCallback(() => {
		return [
			{
				field: 'name',
				header: 'Name',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
				body: nameTemplate,
			},
			{
				field: 'startDaysOut',
				header: 'Start Weeks/Days Out',
				isFeatureEnabled: props.formFilters.isStartDateActive,
				body: startDaysOutTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'targetDaysOut',
				header: 'Target Weeks/Days Out',
				isFeatureEnabled: true,
				body: targetDaysOutTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'startStaticDate',
				header: 'Start Non-Adjusting Date',
				isFeatureEnabled: props.formFilters.isStartDateActive,
				body: startStaticDateTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'targetStaticDate',
				header: 'Target Non-Adjusting Date',
				isFeatureEnabled: true,
				body: targetStaticDateTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'ordinal',
				header: 'Task Order',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
			},
			{
				field: 'baselineJobTitle',
				header: 'Baseline Job Title',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
			},
			{
				field: 'additionalInfo',
				header: 'Additional Information',
				isFeatureEnabled: true,
				default: true,
				body: additionalInfoTemplate,
				isSortable: true,
			},
			{
				field: 'links',
				header: 'Links',
				isFeatureEnabled: true,
				body: linksTemplate,
			},
			{
				field: 'department',
				header: 'Department Name',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
			},
			{
				field: 'discipline',
				header: 'Discipline',
				isFeatureEnabled: true,
				isSortable: true,
			},
			{
				field: 'community',
				header: 'Community',
				isFeatureEnabled: true,
				isSortable: true,
			},
			{
				field: 'phase',
				header: 'Phase',
				isFeatureEnabled: true,
				isSortable: true,
			},
			{
				field: 'anticipatedEffort',
				header: 'Anticipated Effort',
				isFeatureEnabled: props.formFilters.isTaskEffortActive,
				isSortable: true,
			},
			{
				field: 'totalDependencies',
				header: '# of Dependencies',
				isFeatureEnabled: props.formFilters.isTaskDependenciesActive,
				default: true,
				isSortable: true,
			},
			{
				field: 'totalSubtasks',
				header: '# of Steps',
				isFeatureEnabled: props.formFilters.isTaskStepsActive,
				default: true,
				isSortable: true,
			},
			{
				field: 'totalResolvedFeedback',
				header: '# of Resolved Feedback',
				isFeatureEnabled: props.formFilters.isTaskFeedbackActive,
				default: true,
				isSortable: true,
			},
			{
				field: 'createdDate',
				header: 'Created Date',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: createdDateTemplate,
			},
			{
				field: 'modifiedDate',
				header: 'Last Modified',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: modifiedDateTemplate,
			},
			{
				field: 'isActive',
				header: 'Active',
				isFeatureEnabled: true,
				body: activeTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'tags',
				header: 'Tags',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: tagsTemplate,
			},
		];
	}, [props.formFilters]);

	const buildColumnOptions = useCallback(() => {
		const allColumns = getStaticColumns().filter((col) => col.isFeatureEnabled);
		const columnOrder = props.columnOrder;

		let activeColumns: ColumnOption[] = [];

		if (columnOrder && columnOrder.length > 0) {
			activeColumns = columnOrder.map((field) => allColumns.find((col) => col.field === field)).filter((col) => !!col) as ColumnOption[];
		} else {
			activeColumns = allColumns.filter((col) => col.default === true) as ColumnOption[];
		}

		const newColumnOptions = allColumns.map((col) => ({
			label: col.header,
			value: col,
		}));

		setCols(activeColumns);
		setColumnOptions(newColumnOptions as Array<{ label: string; value: ColumnOption }>);
	}, [props.columnOrder, getStaticColumns]);

	const onBaselineSelected = useCallback((id: string | number) => {
		props.updateParamsAndSearch('baselineId', id);
	}, [props]);

	/**
	 * Helper method to detect if the click event originated from a checkbox element
	 * @param {Event} originalEvent - The original DOM event
	 * @returns {boolean} - True if the click was on a checkbox element
	 */
	const isCheckboxClick = useCallback((originalEvent: any) => {
		return (
			originalEvent.target.innerHTML.indexOf('p-checkbox-box') >= 0 ||
			originalEvent.target.className.indexOf('p-checkbox-box') >= 0 ||
			originalEvent.target.className.indexOf('p-checkbox-icon') >= 0 ||
			originalEvent.target.closest('.p-checkbox') !== null
		);
	}, []);

	const onSelectTask = useCallback((e: any) => {
		if (isCheckboxClick(e.originalEvent)) {
			const baselineTaskId = e.value.id;
			props.onToggleCheckedTask(baselineTaskId);
		}
	}, [isCheckboxClick, props]);

	const onRowClick = useCallback((e: any) => {
		// Handle row clicks directly to ensure we can re-select the same task
		if (!isCheckboxClick(e.originalEvent)) {
			const baselineTaskId = e.data.id;
			const rowIndex = props.baselineTasks.indexOf(e.data);

			// Update selection state and always call showTask to handle re-selection
			setSelectedTask(e.data);
			setSelectedRowIndex(rowIndex);

			// Always call showTask to handle both new selections and re-selections
			props.showTask(baselineTaskId);
		}
	}, [isCheckboxClick, props]);

	const showTask = useCallback(() => {
		props.showTask();
	}, [props]);

	const milestoneTemplate = useCallback((rowData: BaselineTask) => {
		if (rowData.isMilestone) {
			return (
				<div className='text-warning milestone' title='Milestone Task'>
					&#x25C6;
				</div>
			);
		}
		return null;
	}, []);

	const activeTemplate = useCallback((rowData: BaselineTask) => {
		if (rowData.isActive) {
			return (
				<div className='text-success text-center'>
					<Icon name='check-circle' />
				</div>
			);
		} else {
			return (
				<div className='text-danger text-center'>
					<Icon name='times-circle' />
				</div>
			);
		}
	}, []);

	const onToggleCheckedTask = useCallback((baselineTask: BaselineTask) => {
		props.onToggleCheckedTask(baselineTask.id);
	}, [props]);

	const checkboxTemplate = useCallback((rowData: BaselineTask) => {
		return <Checkbox name='checkbox' onChange={() => onToggleCheckedTask(rowData)} checked={rowData.IsChecked}></Checkbox>;
	}, [onToggleCheckedTask]);

	const onMasterCheckboxToggle = useCallback(() => {
		props.onMasterCheckboxToggle();
	}, [props]);

	const startDaysOutTemplate = useCallback((rowData: BaselineTask) => {
		if (!rowData.startDaysOut) {
			return null;
		}
		return daysToDaysWeeks(rowData.startDaysOut);
	}, []);

	const startStaticDateTemplate = useCallback((rowData: BaselineTask) => {
		if (rowData.startStaticDate) {
			return Moment.utc(rowData.startStaticDate).format(DATE_FORMAT);
		}

		return '';
	}, []);

	const targetDaysOutTemplate = useCallback((rowData: BaselineTask) => {
		return daysToDaysWeeks(rowData.targetDaysOut);
	}, []);

	const targetStaticDateTemplate = useCallback((rowData: BaselineTask) => {
		if (rowData.targetStaticDate) {
			return Moment.utc(rowData.targetStaticDate).format(DATE_FORMAT);
		} else {
			return null;
		}
	}, []);

	const createdDateTemplate = useCallback((rowData: BaselineTask) => {
		return Moment.utc(rowData.createdDate).format(DATETIME_FORMAT);
	}, []);

	const modifiedDateTemplate = useCallback((rowData: BaselineTask) => {
		return Moment.utc(rowData.modifiedDate).format(DATETIME_FORMAT);
	}, []);

	const linksTemplate = useCallback((rowData: BaselineTask) => {
		let retval: JSX.Element[] = [];

		if (rowData.links && rowData.links.length > 0) {
			for (let i = 0; i < rowData.links.length; i++) {
				const link = rowData.links[i];

				if (link.customerFileId) {
					retval.push(
						<button key={link.ordinal} type='button' className='btn btn-link' onClick={(e) => onDownloadLink(link, e)}>
							{link.name}
						</button>
					);
				} else {
					let protocol = '';
					const linkUrl = link.url.toLowerCase();
					if (!linkUrl.startsWith('http')) protocol = 'http://';

					retval.push(
						<div key={link.ordinal}>
							<a href={protocol + link.url} rel='noopener noreferrer' target='_blank'>
								{link.name}
							</a>
						</div>
					);
				}
			}
		}

		return retval;
	}, []);

	const onDownloadLink = useCallback(async (link: any, e: any) => {
		e.preventDefault();
		try {
			// extract the file name from the link.url
			let start = link.url.indexOf('=') + 1;
			let fileName = link.url.substr(start, link.url.length - start);

			await props.filesActions.download(fileName);
		} catch (error) {
			Notification('error', 'Error', error);
		}
	}, [props]);

	const nameTemplate = useCallback((rowData: BaselineTask) => {
		return <div className='show-newlines'>{rowData.name}</div>;
	}, []);

	const additionalInfoTemplate = useCallback((rowData: BaselineTask) => {
		return <div className='show-newlines'>{TruncateText(rowData.additionalInfo, 200)}</div>;
	}, []);

	const tagsTemplate = useCallback((rowData: BaselineTask) => {
		const retval: JSX.Element[] = [];

		if (rowData.tags && rowData.tags.length > 0) {
			for (let i = 0; i < rowData.tags.length; i++) {
				const tag = rowData.tags[i];

				retval.push(
					<div className='mb-1' key={tag.name}>
						<Tag title={tag.name} color={tag.fontColor} backgroundColor={tag.backgroundColor} />
					</div>
				);
			}
		}
		return retval;
	}, []);

	const onTextFilterChange = useCallback((e: any) => {
		props.updateSearchParams(e.target.name, e.target.value);
	}, [props]);

	const onSearchBlur = useCallback(() => {
		props.updateParamsAndSearch();
	}, [props]);

	const keyUpFilter = useCallback((e: any) => {
		if (e.keyCode == 13) {
			props.updateParamsAndSearch();
		}
	}, [props]);

	const onActiveChange = useCallback((e: any) => {
		const selectedValue = getNullableOnChangeValue(e);
		props.updateParamsAndSearch('isActive', selectedValue);
	}, [props]);

	const onPreviewPushdown = useCallback((open: boolean) => {
		setShowPreviewDrawer(open);
	}, []);

	const onBulkPushdown = useCallback(() => {
		setIsBulkPushdownDrawerOpen(true);
	}, []);

	const onBulkPushdownComplete = useCallback(() => {
		setIsBulkPushdownDrawerOpen(false);
		props.onBulkPushdownComplete();
	}, [props]);

	const onBulkPushdownCancel = useCallback(() => {
		setIsBulkPushdownDrawerOpen(false);
	}, []);

	const onBulkUpdateAttributes = useCallback(() => {
		setIsBulkUpdateAttributesDrawerOpen(true);
	}, []);

	const onBulkUpdateAttributesComplete = useCallback(() => {
		props.onBulkPushdownComplete();
		setIsBulkUpdateAttributesDrawerOpen(false);
	}, [props]);

	const onBulkUpdateAttributesCancel = useCallback(() => {
		setIsBulkUpdateAttributesDrawerOpen(false);
	}, []);

	const onImportClick = useCallback(() => {
		setIsImportDrawerOpen(true);
	}, []);

	const onColumnsClick = useCallback(() => {
		setIsColumnDrawerOpen(true);
	}, []);

	const onColumnsClose = useCallback(() => {
		setIsColumnDrawerOpen(false);
	}, []);

	const onColumnToggle = useCallback(async (name: string, value: boolean) => {
		let columns = [...cols];
		let modifiedColumn = columnOptions.find((m) => m.value.field === name);

		if (!modifiedColumn) return;

		if (value) {
			if (!columns.some((col) => col.field === name)) {
				columns.push(modifiedColumn.value);
			}
		} else {
			columns = columns.filter((col) => col.field !== name);
		}

		const orderedColumnFields = ['isChecked', ...columns.map((col) => col.field)];

		setCols(columns);
		await props.userActions.saveUsersColumns({ columns: orderedColumnFields, columnsType: SortColumnsType.AdminPageBaselineTasks });
		props.onColumnOrderChange(orderedColumnFields);
	}, [cols, columnOptions, props]);

	const onExportClick = useCallback(() => {
		const params = [
			{
				key: REPORT_URL_PARAMS.BaselineId.toString(),
				value: props.searchParams.baselineId,
			},
		];
		const route = Routes.Reports + '/' + REPORT_IDS.BaselineTaskList;
		RouteWithParams(route, params, false);
	}, [props]);

	const onImportCancel = useCallback(() => {
		setIsImportDrawerOpen(false);
	}, []);

	const onImportComplete = useCallback(() => {
		props.updateParamsAndSearch();
		setIsImportDrawerOpen(false);
	}, [props]);

	const listingRefresh = useCallback(() => {
		props.onComplete();
	}, [props]);

	const onShowDateRangePicker = useCallback((rangeName: string) => {
		let start: any = null;
		let end: any = null;

		start = props.searchParams.createdDateStart ? props.searchParams.createdDateStart : null;
		end = props.searchParams.createdDateEnd ? props.searchParams.createdDateEnd : null;

		if (typeof end === 'string') {
			end = Moment.utc(Date.parse(end));
		}
		if (typeof start === 'string') {
			start = Moment.utc(Date.parse(start));
		}

		const newDateRange = {
			...dateRange,
			name: rangeName,
			start: start?.toDate() || start,
			end: end?.toDate() || end,
		};
		setShowDateRangePicker(true);
		setDateRange(newDateRange);
	}, [props, dateRange]);

	// the event that fires when a new date in the range picker is selected
	// remember these so we can add to search params if they hit the Apply button on the modal
	const onStartRangeChange = useCallback((startDate: Date, endDate: Date) => {
		const newDateRange = {
			...dateRange,
			start: startDate,
			end: endDate,
		};
		setDateRange(newDateRange);
	}, [dateRange]);

	// the event that fires when the Apply button is clicked in a date range modal
	const onSelectDateRange = useCallback(async () => {
		setShowDateRangePicker(false);
		const start = Moment.utc(dateRange.start);
		const end = Moment.utc(dateRange.end);
		props.updateCreatedDateParams(start.toDate(), end.toDate());
	}, [dateRange, props]);

	const onCancelDateRange = useCallback(() => {
		setShowDateRangePicker(false);
	}, []);

	const onChangeOpeningDate = useCallback((dateString: string, name: string) => {
		setOpeningDate(new Date(dateString));
	}, []);

	const handleMultiSelectChange = useCallback((key: string) => (e: any) => {
		props.updateSearchParams(key, getMultiSelectFilterValues(e.value));
		setDropFilterChanged(true);
	}, [props]);

	const handleMultiSelectClose = useCallback(() => {
		if (dropFilterChanged === true) {
			props.updateParamsAndSearch();
			setDropFilterChanged(false);
		}
	}, [dropFilterChanged, props]);

	const customCloseIcon = useCallback(() => {
		return (
			<i
				className={classNames('pi', {
					'pi-times': !dropFilterChanged,
					'pi-check-square text-success': dropFilterChanged,
				})}
			/>
		);
	}, [dropFilterChanged]);

	const getColumnFilterMultiSelectValues = useCallback((sourceIds: number[], columnFiltersValues: FilterOption[]) => {
		let results: FilterOption[] = [];

		if (!sourceIds || sourceIds.length === 0) return [];

		if (columnFiltersValues && columnFiltersValues.length > 0) {
			for (let i = 0; i < sourceIds.length; i++) {
				const value = columnFiltersValues.find((m) => m.id.toString() === sourceIds[i].toString());
				if (value) {
					results.push(value);
				}
			}
		}

		return results;
	}, []);

	const renderColumns = useCallback(() => {
		const activeOptions = [
			{ label: 'Yes', value: true },
			{ label: 'No', value: false },
			{ label: 'All', value: null },
		];

		const baselineJobTitlesValues = getColumnFilterMultiSelectValues(
			props.searchParams.baselineJobTitles,
			props.baselineJobTitles
		);
		const departmentValues = getColumnFilterMultiSelectValues(props.searchParams.departments, props.departments);
		const communitiesValues = getColumnFilterMultiSelectValues(props.searchParams.communities, props.communities);
		const phasesValues = getColumnFilterMultiSelectValues(props.searchParams.phases, props.phases);
		const disciplinesValues = getColumnFilterMultiSelectValues(props.searchParams.disciplines, props.disciplines);
		const tagsValues = getColumnFilterMultiSelectValues(props.searchParams.tags, props.tags);

		const filters = {
			name: (
				<input
					type='text'
					name='name'
					className='p-inputtext'
					size={50}
					onBlur={onSearchBlur}
					onChange={onTextFilterChange}
					onKeyUp={keyUpFilter}
					value={props.searchParams.name}
					placeholder='Name'
				/>
			),
			additionalInfo: (
				<input
					type='text'
					name='additionalInfo'
					className='p-inputtext'
					size={50}
					onBlur={onSearchBlur}
					onChange={onTextFilterChange}
					onKeyUp={keyUpFilter}
					value={props.searchParams.additionalInfo}
					placeholder='Additional Info'
				/>
			),
			department: (
				<MultiSelect
					value={departmentValues}
					onChange={handleMultiSelectChange('departments')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.departments}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={customCloseIcon()}
				/>
			),
			baselineJobTitle: (
				<MultiSelect
					value={baselineJobTitlesValues}
					onChange={handleMultiSelectChange('baselineJobTitles')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.baselineJobTitles}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={customCloseIcon()}
				/>
			),
			community: (
				<MultiSelect
					value={communitiesValues}
					onChange={handleMultiSelectChange('communities')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.communities}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={customCloseIcon()}
				/>
			),
			phase: (
				<MultiSelect
					value={phasesValues}
					onChange={handleMultiSelectChange('phases')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.phases}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={customCloseIcon()}
				/>
			),
			discipline: (
				<MultiSelect
					value={disciplinesValues}
					onChange={handleMultiSelectChange('disciplines')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.disciplines}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={customCloseIcon()}
				/>
			),
			isActive: (
				<Dropdown
					className='ui-column-filter'
					value={props.searchParams.isActive}
					options={activeOptions}
					optionLabel='label'
					optionValue='value'
					onChange={onActiveChange}
					placeholder='Select an option'
				/>
			),
			createdDate: (
				<Button
					type='button'
					bootstrapClass='light ui-column-filter'
					name='createdDateRangeButton'
					text={props.searchParams.createdDateStart || props.searchParams.createdDateEnd ? 'Edit Range' : 'Range'}
					onClick={() => onShowDateRangePicker('createdDate')}
				/>
			),
			tags: (
				<MultiSelect
					value={tagsValues}
					onChange={handleMultiSelectChange('tags')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.tags}
					placeholder='Filter'
					maxSelectedLabels={1}
					closeIcon={customCloseIcon()}
				/>
			),
		};

		let columns = cols.map((col) => {
			const filterElement = filters[col.field];
			// HACK: (jwv) PrimeReact's MultiSelect is throwing an error any time we deselect a column from the column selector
			// when it's internal object comparison code tries to set the _$visible property on a style object defined in our
			// columns objects.  IsActive was the only column with style object, and it was setting the width to 80px, so this
			// hack is in place to explicitly set that on the column here, instead of definining the style object as a member
			// of the column definition.
			if (col.field === 'isActive') {
				return (
					<Column
						key={col.field}
						sortable={col.isSortable}
						field={col.field}
						filter={true}
						filterElement={filterElement}
						header={col.header}
						body={col.body}
						style={{ width: '80px' }}
					/>
				);
			} else if (filterElement && col.isFeatureEnabled) {
				return (
					<Column
						key={col.field}
						sortable={col.isSortable}
						field={col.field}
						filter={true}
						filterElement={filterElement}
						header={col.header}
						body={col.body}
					/>
				);
			} else if (col.isFeatureEnabled) {
				return <Column key={col.field} sortable={col.isSortable} field={col.field} header={col.header} body={col.body} />;
			} else {
				return null;
			}
		});

		return columns;
	}, [cols, props, getColumnFilterMultiSelectValues, onSearchBlur, onTextFilterChange, keyUpFilter, handleMultiSelectChange, handleMultiSelectClose, onActiveChange, showDateRangePicker, customCloseIcon]);

	const onSort = useCallback(({ sortField, sortOrder }: any) => {
		const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);

		setSelectedRowIndex(null);	// Reset selected row index & task
		setSelectedTask(null);
		props.onSort(sortBy, sortDir);
	}, [props]);

	const onPageChange = useCallback((e: any) => {
		setSelectedRowIndex(null);	// Reset selected row index & task
		setSelectedTask(null);
		props.updateParamsAndSearch('pageNumber', e.page + 1);
	}, [props]);

	const onOrderTasksClick = useCallback(() => {
		const tasks = props.checkedTasks.slice();
		const targetDaysOut = tasks[0].targetDaysOut;
		const targetStaticDate = tasks[0].targetStaticDate;
		if (tasks.length > 0) {
			for (let i = 1; i < tasks.length; i++) {
				const task = tasks[i];
				const invalidDaysOut = task.targetDaysOut !== targetDaysOut;
				const invalidStaticDate = task.targetStaticDate !== targetStaticDate;
				if (invalidDaysOut || invalidStaticDate) {
					Notification(
						'error',
						'Error',
						'To use the Order Tasks feature, the selected tasks must be on the same Weeks/Days Out. Please reselect one or more tasks.'
					);
					return;
				}
			}
		}

		setIsOrderTasksDrawerOpen(true);
		setOrderTasksTargetDaysOut(targetDaysOut.toString());
		setOrderTasksTargetStaticDate(targetStaticDate || '');
	}, [props.checkedTasks]);

	const onOrderTasksComplete = () => {
		props.clearAllCheckedTasks();
		setIsOrderTasksDrawerOpen(false);
		setSelectedRowIndex(null); // Reset selected row index & task
		setSelectedTask(null);
		props.updateParamsAndSearch('refreshKey', Date.now());
	};

	const onOrderTasksCancel = useCallback(() => {
		setIsOrderTasksDrawerOpen(false);
	}, []);

	const onCancelOpeningDateModal = useCallback(() => {
		setShowOpeningDateModal(false);
	}, []);

	const onCompleteOpeningDateModal = useCallback((openingDate: string) => {
		ReactGA.event({ category: 'Baseline Task List Page', action: 'Toggle Gantt Chart' });
		setOpeningDate(new Date(openingDate));
		setShowOpeningDateModal(false);
		props.onViewSelectorChange(VIEWS.Gantt.value);
	}, [props.onViewSelectorChange]);

	const onGanttZoomIn = useCallback(() => {
		if (ganttZoomLevel < GANTT_ZOOM_LEVELS.Day.value) {
			setGanttZoomLevel(ganttZoomLevel + 1);
		}
	}, [ganttZoomLevel]);

	const onGanttZoomOut = useCallback(() => {
		if (ganttZoomLevel > GANTT_ZOOM_LEVELS.Month.value) {
			setGanttZoomLevel(ganttZoomLevel - 1);
		}
	}, [ganttZoomLevel]);

	const onViewChange = useCallback((viewName: string) => {
		if (viewName === VIEWS.List.value) {
			// do nothing
		} else if (viewName === VIEWS.Gantt.value) {
			if (openingDate === null) {
				setShowOpeningDateModal(true);
				return; // return because we don't want to switch views until they select an opening date (they could cancel)
			}
		}

		props.onViewSelectorChange(viewName);
	}, [openingDate, props.onViewSelectorChange]);

	const onColumnReorder = useCallback(async (e: any) => {
		const reorderedFields = e.columns.map((c: any) => c.props.field);

		// isChecked/isMilestone are rendered outside the configurable set, so they must not be saved.
		const filtered = reorderedFields.filter((f: string) => f !== 'isChecked' && f !== 'isMilestone');

		const corrected = ['isChecked', ...filtered];

		await props.userActions.saveUsersColumns({ columns: corrected, columnsType: SortColumnsType.AdminPageBaselineTasks });
		props.onColumnOrderChange(corrected);
	}, [props.userActions, props.onColumnOrderChange]);

	const rowClass = useCallback((data: BaselineTask) => {
		return {
			'p-highlight': data.id === selectedTask?.id,
		};
	}, [selectedTask]);

	// Convert the render method to a return statement

	const { sortField, sortOrder } = getDataTableSortFields(props.searchParams);

	const viewOptions = [
		{ id: VIEWS.List.value, name: VIEWS.List.label, icon: VIEWS.List.icon },
		{ id: VIEWS.Gantt.value, name: VIEWS.Gantt.label, icon: VIEWS.Gantt.icon },
	];

	const masterCheckbox = (
		<div>
			<Checkbox name='checkbox' onChange={() => onMasterCheckboxToggle()} checked={props.allTasksChecked}></Checkbox>
		</div>
	);

	const filterButtonClass = props.hasNoFilters() ? '' : ' text-primary';

	const isBulkMode = props.checkedTasks && props.checkedTasks.length > 0;

	const toolbarCols = props.selectedView === VIEWS.Gantt.value ? ' col-md-7' : ' col-md-8';
	return (
		<div className='task-list-wrapper'>
			{/* isLoadingBaselines (not isFetchingBaselines): without hideWhileLoading the children
			    stay mounted, but the fetching flag would still overlay a spinner on the whole page
			    every time the baseline list refetches on window refocus. */}
			<LoadingIndicator isLoading={props.isLoadingBaselines}>
				<div className='task-list-header row'>
					<div className='col-md-4'>
						<div className='float-start w-100'>
							<MultiSelectBox
								name='baselineId'
								label='Baseline'
								idField='id'
								valueField='name'
								options={props.baselines}
								onChange={onBaselineSelected}
								selectedValues={props.searchParams.baselineId.toString()}
								isRequired={true}
								errorMessage='Baseline is required'
								isMulti={false}
							/>
						</div>
					</div>
					{props.selectedView === VIEWS.Gantt.value && (
						<div className='col-md-1'>
							<DatePicker
								className={'opening-date-picker'}
								value={openingDate?.toISOString().split('T')[0] || ''}
								name='openingDate'
								label='Opening Date'
								onChange={onChangeOpeningDate}
							/>
						</div>
					)}
					{Number(props.searchParams.baselineId) > 0 ? (
						<div className={'task-list-toolbar' + toolbarCols}>
							{isBulkMode && (
								<Fragment>
									<div className='icon' onClick={onBulkUpdateAttributes}>
										<Icon name='pencil-alt' />
										Update Attributes
									</div>
									<div className='icon' onClick={onBulkPushdown}>
										<Icon name='tasks' />
										Bulk Pushdown
									</div>
									<div className='icon' onClick={() => onPreviewPushdown(true)}>
										<Icon name='tasks' />
										Preview Pushdown
									</div>
									<div className='icon' onClick={onOrderTasksClick}>
										<Icon name='arrows-v' />
										Order Tasks
									</div>
								</Fragment>
							)}
							{!isBulkMode && (
								<Fragment>
									<MultiSelectBox
										name='views'
										idField='id'
										valueField='name'
										className='view-selector'
										options={viewOptions}
										onChange={onViewChange}
										selectedValues={props.selectedView}
										isMulti={false}
										isSearchable={false}
									/>
									<div className='d-inline'>
										<Icon name='filter' />
										<div className={'show-filters' + filterButtonClass} onClick={props.onViewFiltersClick}>
											Show Filters
										</div>
										<div className='d-inline'>|</div>
										<div className={'clear-filters' + filterButtonClass} onClick={props.clearFilters}>
											Clear Filters
										</div>
									</div>
									{props.selectedView != VIEWS.Gantt.value ? (
										<Fragment>
											<div className='icon' onClick={onColumnsClick}>
												<Icon name='eye' /> Columns
											</div>
											<div className='icon' onClick={onExportClick}>
												<Icon name='file-excel' />
												Export
											</div>
											<div className='icon' onClick={onImportClick}>
												<Icon name='file-code' />
												Import
											</div>
										</Fragment>
									) : (
										<div className='d-inline'>
											<div className='icon zoom-in' onClick={onGanttZoomIn}>
												<Icon name='search-plus' stylePrefix='far' />
												Zoom In
											</div>
											<div className='icon zoom-out' onClick={onGanttZoomOut}>
												<Icon name='search-minus' stylePrefix='far' />
												Zoom Out
											</div>
										</div>
									)}
								</Fragment>
							)}
							<button type='button' className='btn btn-primary' onClick={showTask} title='Add Baseline Task'>
								<span className='me-2'>Add Baseline Task</span>
								<Icon name='plus' />
							</button>
						</div>
					) : (
						''
					)}
				</div>

				{/* isLoading* (not isFetching*) below: hideWhileLoading unmounts the grid, so a
				    background-refetch flag there would destroy and rebuild the table on every 30s
				    poll and on every window refocus, losing scroll position and column widths. */}
				{props.selectedView === VIEWS.List.value && Number(props.searchParams.baselineId) > 0 && (
					<LoadingIndicator isLoading={props.isLoadingTasks || props.isLoadingFilters} hideWhileLoading={true}>
						<div className='task-list-results-table'>
							<DataTable
								ref={dataTableRef}
								dataKey='id'
								filterDisplay='row'
								className='p-datatable-sm p-datatable-striped'
								value={props.baselineTasks}
								selectionMode='single'
								selection={selectedTask}
								onSelectionChange={onSelectTask}
								onRowClick={onRowClick}
								rowClassName={rowClass}
								reorderableColumns
								onColReorder={(e) => onColumnReorder(e)}
								sortField={sortField}
								sortOrder={sortOrder as any}
								onSort={onSort}
								scrollable={true}
								scrollHeight="flex"
								style={{ minWidth: '100%' }}>
								<Column
									field='isChecked'
									filter={true}
									filterElement={masterCheckbox}
									body={checkboxTemplate}
									style={{ textAlign: 'center', width: '40px' }}
								/>
								<Column field='isMilestone' body={milestoneTemplate} style={{ textAlign: 'center', width: '40px' }} />
								{renderColumns()}
							</DataTable>
							{props.totalCount > props.searchParams.pageSize && (
								<Paginator
									rows={props.searchParams.pageSize}
									totalRecords={props.totalCount}
									first={(props.searchParams.pageNumber - 1) * props.searchParams.pageSize}
									onPageChange={onPageChange}
								/>
							)}
							<div className='results-count'>Total records: {props.totalCount}</div>
						</div>
					</LoadingIndicator>
				)}
				{props.selectedView === VIEWS.Gantt.value && (
					<GanttChartView
						subscriptionId={props.subscriptionId}
						onSelectTask={props.showTask}
						searchParams={props.searchParams}
						lastFilterChange={props.lastFilterChange}
						openingDate={openingDate ? openingDate.toISOString() : null}
						zoomLevel={ganttZoomLevel}
						isTaskDependenciesActive={props.formFilters.isTaskDependenciesActive}
						isStartDateActive={props.formFilters.isStartDateActive}
					/>
				)}
			</LoadingIndicator>

			{showPreviewDrawer ? (
				<PushdownPreviewDrawer
					onComplete={() => onPreviewPushdown(false)}
					baselineId={props.searchParams.baselineId.toString()}
					isProjectWidgetActive={props.formFilters.isProjectWidgetActive}
				/>
			) : (
				''
			)}

			{isBulkPushdownDrawerOpen ? (
				<BulkPushdownDrawer
					baselineId={props.searchParams.baselineId.toString()}
					checkedTasks={props.checkedTasks}
					onComplete={onBulkPushdownComplete}
					onCancel={onBulkPushdownCancel}
				/>
			) : null}
			{isBulkUpdateAttributesDrawerOpen && (
				<BulkUpdateAttributesDrawer
					baselineId={props.searchParams.baselineId.toString()}
					checkedTasks={props.checkedTasks}
					onComplete={onBulkUpdateAttributesComplete}
					onCancel={onBulkUpdateAttributesCancel}
				/>
			)}
			{isImportDrawerOpen ? (
				<BaselineImportDrawer
					subscriptionId={props.subscriptionId}
					baselineId={props.searchParams.baselineId.toString()}
					onCancel={onImportCancel}
					onComplete={onImportComplete}
				/>
			) : null}
			{isOrderTasksDrawerOpen && (
				<OrderTasksDrawer
					subscriptionId={props.subscriptionId}
					baselineId={props.searchParams.baselineId.toString()}
					targetDaysOut={orderTasksTargetDaysOut?.toString()}
					targetStaticDate={orderTasksTargetStaticDate}
					onCancel={onOrderTasksCancel}
					onComplete={onOrderTasksComplete}
				/>
			)}
			{isBulkMode && (
				<Dockbar>
					<ActiveToggleForm
						tasksChecked={props.baselineTasks.filter((bt) => bt.IsChecked === true)}
						listingRefresh={listingRefresh}
					/>
				</Dockbar>
			)}
			{isColumnDrawerOpen && (
				<BaselineTaskListColumnDrawer
					onComplete={onColumnsClose}
					onColumnToggle={onColumnToggle}
					columnOptions={columnOptions}
					columns={cols}
				/>
			)}
			{showDateRangePicker ? (
				<Modal
					id='startDateRangeModal'
					title='Select Date Range'
					body={
						<div className='datepicker-container'>
							<DateRangePickerWrapper
								onDatesChange={onStartRangeChange}
								initialStartDate={dateRange.start}
								initialEndDate={dateRange.end}
							/>
						</div>
					}
					primaryButtonText='Apply'
					onPrimaryClick={onSelectDateRange}
					onSecondaryClick={onCancelDateRange}
					onCloseClick={onCancelDateRange}
				/>
			) : null}
			{showOpeningDateModal && (
				<OpeningDateModal onCancel={onCancelOpeningDateModal} onComplete={onCompleteOpeningDateModal} />
			)}
		</div>
	);
};

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		userActions: bindActionCreators(userActions, dispatch),
	};
}

export default withRouter(connect(null, mapDispatchToProps)(BaselineTaskListDesktop));
